import React, { useState, useEffect, useCallback } from "react";
import { Modal, DatePicker, Select, Spin, Card, Statistic, Row, Col, Alert } from "antd";
import { apiRequests } from "src/shared/api/api";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const { Option } = Select;

const PayboxTurnover = ({ visible, onCancel, token, payboxes, initialPayboxId = null, initialPayboxLabel = null }) => {
  const [loading, setLoading] = useState(false);
  const [payboxId, setPayboxId] = useState(initialPayboxId);
  const [payboxLabel, setPayboxLabel] = useState(initialPayboxLabel);
  const [dateRange, setDateRange] = useState([dayjs().startOf('day'), dayjs().endOf('day')]);
  const [turnoverData, setTurnoverData] = useState(null);
  const [error, setError] = useState(null);

  const fetchTurnoverData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Получаем данные за выбранный период
      let params = {
        token: token,
        limit: 9999,
        offset: 0,
        relship: 'all',
        datefrom: dateRange[0].format('DD-MM-YYYY'),
        dateto: dateRange[1].format('DD-MM-YYYY'),
      };
      
      if (payboxId) {
        const payboxName = payboxLabel || payboxes?.find(p => p.key === payboxId)?.props.children[0];
        
        if (payboxName) {
          params.paybox = payboxName;
          
          params.include_paybox_dest = true;
        }
      }

      const startDateParams = {
        ...params,
        dateto: dateRange[0].subtract(1, 'day').format('DD-MM-YYYY')
      };
      delete startDateParams.datefrom;

      const [periodResponse, beforePeriodResponse] = await Promise.all([
        apiRequests.payments.getItems(params),
        apiRequests.payments.getItems(startDateParams)
      ]);

      if (periodResponse.data && periodResponse.data.result) {
        const payments = periodResponse.data.result;
        const beforePeriodPayments = beforePeriodResponse.data?.result || [];
        
        // Расчет оборота
        let income = 0;          // Приход (без переводов)
        let expense = 0;         // Расход (без переводов)
        let transferIn = 0;      // Пришло переводом
        let transferOut = 0;     // Ушло переводом
        
        const payboxIdNumber = payboxId ? parseInt(payboxId) : null;
        
        payments.forEach(payment => {
          // Учитываем только подтвержденные операции
          if (payment.status) {
            const amount = parseFloat(payment.amount);
            
            if (payment.type === "incoming") {
              income += amount;
            } else if (payment.type === "outgoing") {
              expense += amount;
            } else if (payment.type === "transfer") {
              // Для переводов необходимо определить направление
              if (payboxIdNumber) {
                // Если перевод поступил на текущий счет
                if (payment.paybox_to === payboxIdNumber) {
                  transferIn += amount;
                }
                // Если перевод ушел с текущего счета
                else if (payment.paybox === payboxIdNumber) {
                  transferOut += amount;
                }
              }
            }
          }
        });
        
        // Расчет остатка на начало периода
        let startBalance = 0;
        
        beforePeriodPayments.forEach(payment => {
          if (payment.status) {
            const amount = parseFloat(payment.amount);
            
            if (payment.type === "incoming") {
              startBalance += amount;
            } else if (payment.type === "outgoing") {
              startBalance -= amount;
            } else if (payment.type === "transfer") {
              // Определяем направление перевода по ID счетов
              if (payboxIdNumber) {
                // Если перевод поступил на текущий счет
                if (payment.paybox_to === payboxIdNumber) {
                  startBalance += amount;
                }
                // Если перевод ушел с текущего счета
                else if (payment.paybox === payboxIdNumber) {
                  startBalance -= amount;
                }
              }
            }
          }
        });

        // Итоговые показатели
        const totalIncome = income + transferIn;      // Итого пришло
        const totalExpense = expense + transferOut;   // Итого ушло
        const endBalance = startBalance + totalIncome - totalExpense;  // Остаток на конец периода
        
        setTurnoverData({
          startBalance,            // На начало периода
          endBalance,              // На конец периода
          currentBalance: endBalance, // Текущий остаток (для выбранного периода = конечному)
          income,                  // Приход (без переводов)
          expense,                 // Расход (без переводов)
          transferIn,              // Пришло переводом
          transferOut,             // Ушло переводом
          totalIncome,             // Итого пришло
          totalExpense,            // Итого ушло
          count: payments.length
        });
      }
    } catch (error) {
      console.error("Failed to fetch turnover data:", error);
      setError("Не удалось получить данные об обороте");
    } finally {
      setLoading(false);
    }
  }, [token, payboxId, payboxLabel, dateRange, payboxes]);

  useEffect(() => {
    if (visible) {
      fetchTurnoverData();
    }
  }, [visible, fetchTurnoverData]);

  const handlePayboxChange = (value) => {
    setPayboxId(value);
    if (value) {
      const paybox = payboxes?.find(p => p.key === value);
      if (paybox) {
        setPayboxLabel(paybox.props.children[0] || null);
      } else {
        setPayboxLabel(null);
      }
    } else {
      setPayboxLabel(null);
    }
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  return (
    <Modal
      title="Оборот по счету"
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={700}
    >
      <div style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Select
              placeholder="Выберите счет"
              style={{ width: '100%' }}
              onChange={handlePayboxChange}
              value={payboxId}
              allowClear
            >
              {payboxes?.map(paybox => (
                <Option key={paybox.key} value={paybox.key}>{paybox.props.children[0]}</Option>
              ))}
            </Select>
          </Col>
          <Col span={12}>
            <RangePicker
              style={{ width: '100%' }}
              value={dateRange}
              onChange={handleDateRangeChange}
              format="DD-MM-YYYY"
            />
          </Col>
        </Row>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
        </div>
      ) : error ? (
        <Alert type="error" message={error} />
      ) : turnoverData && (
        <Card>
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <Statistic 
                title="На начало периода" 
                value={turnoverData.startBalance} 
                precision={2} 
                suffix="руб."
                valueStyle={{ color: turnoverData.startBalance >= 0 ? '#3f8600' : '#cf1322' }}
              />
            </Col>
            <Col span={8}>
              <Statistic 
                title="На конец периода" 
                value={turnoverData.endBalance} 
                precision={2} 
                suffix="руб."
                valueStyle={{ color: turnoverData.endBalance >= 0 ? '#3f8600' : '#cf1322' }}
              />
            </Col>
            <Col span={8}>
              <Statistic 
                title="Текущий остаток" 
                value={turnoverData.currentBalance} 
                precision={2} 
                suffix="руб."
                valueStyle={{ color: turnoverData.currentBalance >= 0 ? '#3f8600' : '#cf1322' }}
              />
            </Col>
            
            <Col span={12}>
              <Statistic 
                title="Приход" 
                value={turnoverData.income} 
                precision={2} 
                suffix="руб."
                valueStyle={{ color: '#3f8600' }}
              />
            </Col>
            <Col span={12}>
              <Statistic 
                title="Расход" 
                value={turnoverData.expense} 
                precision={2} 
                suffix="руб."
                valueStyle={{ color: '#cf1322' }}
              />
            </Col>
            
            <Col span={12}>
              <Statistic 
                title="Пришло переводом" 
                value={turnoverData.transferIn} 
                precision={2} 
                suffix="руб."
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
            <Col span={12}>
              <Statistic 
                title="Ушло переводом" 
                value={turnoverData.transferOut} 
                precision={2} 
                suffix="руб."
                valueStyle={{ color: '#fa8c16' }}
              />
            </Col>
            
            <Col span={12}>
              <Statistic 
                title="Итого пришло" 
                value={turnoverData.totalIncome} 
                precision={2} 
                suffix="руб."
                valueStyle={{ color: '#3f8600', fontWeight: 'bold' }}
              />
            </Col>
            <Col span={12}>
              <Statistic 
                title="Итого ушло" 
                value={turnoverData.totalExpense} 
                precision={2} 
                suffix="руб."
                valueStyle={{ color: '#cf1322', fontWeight: 'bold' }}
              />
            </Col>
          </Row>
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <p>Всего операций за период: {turnoverData.count}</p>
            <p>
              {payboxLabel ? 
                `Счет: ${payboxLabel}` : 
                payboxId ? 
                  `Счет: ${payboxes?.find(p => p.key === payboxId)?.props.children[0] || 'Не указан'}` :
                  'Все счета'
              }
            </p>
          </div>
        </Card>
      )}
    </Modal>
  );
};

export default PayboxTurnover; 